import type {Observable} from 'rxjs';
import {ajax} from 'rxjs/ajax';
import type {IHttpParamSerializer} from 'angular';
import {map} from 'rxjs/operators';
import type {AnyObject} from 'typings/portaro.fe.types';

const DEFAULT_CONFIGURATION: ObservableAjaxConfiguration = {
    returnRawAjaxResponse: false
};

/**
 * @ngdoc provider
 * @name observableAjaxServiceProvider
 * @module portaro.data-services
 *
 * @description
 * Provider for {@link service:observableAjaxService}
 */
export default class ObservableAjaxServiceProvider {
    public static providerName = 'observableAjaxService';

    public readonly $get: (_: IHttpParamSerializer) => ObservableAjaxService;

    private baseUrl: string;
    private defaultConfiguration: ObservableAjaxConfiguration;

    constructor() {
        this.defaultConfiguration = DEFAULT_CONFIGURATION;
        this.$get = /*@ngInject*/ ($httpParamSerializer: IHttpParamSerializer) => this.createObservableAjaxService($httpParamSerializer);
    }

    /**
     * @ngdoc method
     * @name observableAjaxServiceProvider#setBaseUrl
     *
     * @param {string} baseUrl Base URL
     *
     * @description
     * Sets base url for all AJAX requests. E.g.: if you have your API with `/api` prefix, it can be set here.
     */
    public setBaseUrl(baseUrl: string) {
        this.baseUrl = baseUrl;
    }

    /**
     * @ngdoc method
     * @name observableAjaxServiceProvider#configure
     *
     * @param {ObservableAjaxConfiguration} ajaxConfiguration Global configuration
     *
     * @description
     * You can set global configuration in this method.
     */
    public configure(ajaxConfiguration: ObservableAjaxConfiguration) {
        this.defaultConfiguration = Object.assign(DEFAULT_CONFIGURATION, ajaxConfiguration);
    }

    private createObservableAjaxService($httpParamSerializer: IHttpParamSerializer): ObservableAjaxService {
        return {
            forUri: (uri: string) => {
                const requestUrl = `${this.baseUrl}/${uri}`;
                return new AjaxRequestBuilderImpl(requestUrl, this.defaultConfiguration, $httpParamSerializer);
            }
        };
    }
}

/**
 * @ngdoc service
 * @name observableAjaxService
 * @module portaro.data-services
 *
 * @description
 * Service for easy handling with Observable AJAX requests.
 *
 * See {@link type:AjaxRequestBuilder} for more methods
 */
export interface ObservableAjaxService {

    /**
     * @ngdoc method
     * @name observableAjaxService#forUri
     *
     * @param {string} uri URI to append after `baseUrl` set in {@link provider:observableAjaxServiceProvider}
     * @returns {AjaxRequestBuilder} Request builder
     */
    forUri(uri: string): AjaxRequestBuilder;
}

/**
 * @ngdoc type
 * @name AjaxRequestBuilder
 * @module portaro.data-services
 *
 * @description
 * Request builder
 */
export interface AjaxRequestBuilder {

    /**
     * @ngdoc method
     * @name AjaxRequestBuilder#getHeaders
     *
     * @description
     * Returns request headers
     *
     * @returns {AnyObject}
     */
    getHeaders(): AnyObject;

    /**
     * @ngdoc method
     * @name AjaxRequestBuilder#getResponseType
     *
     * @description
     * Returns responseType
     *
     * @returns {XMLHttpRequestResponseType}
     */
    getResponseType(): string;

    /**
     * @ngdoc method
     * @name AjaxRequestBuilder#getBody
     *
     * @description
     * Returns request body
     * @returns {any}
     */
    getBody(): any;

    /**
     * @ngdoc method
     * @name AjaxRequestBuilder#getMethod
     *
     * @description
     * Returns request method. `'get'` | `'post'` | `'put'` | `'delete'`.
     *
     * @returns {AjaxRequestMethod}
     */
    getMethod(): AjaxRequestMethod;

    /**
     * @ngdoc method
     * @name AjaxRequestBuilder#getParams
     *
     * @description
     * Returns params
     *
     * @returns {any}
     */
    getParams(): any;

    /**
     * @ngdoc method
     * @name AjaxRequestBuilder#getUrl
     *
     * @description
     * Returns full URL
     *
     * @returns {string}
     */
    getUrl(): string;

    /**
     * @ngdoc method
     * @name AjaxRequestBuilder#withConfiguration
     *
     * @description
     * Add specified advanced configuration
     *
     * @param {ObservableAjaxConfiguration} configuration Configuration
     * @returns {AjaxRequestBuilder}
     */
    withConfiguration(configuration: ObservableAjaxConfiguration): AjaxRequestBuilder;

    /**
     * @ngdoc method
     * @name AjaxRequestBuilder#withHeaders
     *
     * @description
     * Add custom request headers
     *
     * @param {object} headers Request headers
     * @returns {AjaxRequestBuilder}
     */
    withHeaders(headers: AnyObject): AjaxRequestBuilder;

    /**
     * @ngdoc method
     * @name AjaxRequestBuilder#withResponseType
     *
     * @description
     * Add responseType. By default is `'json'`.
     *
     * @param {XMLHttpRequestResponseType} responseType Response type
     * @returns {AjaxRequestBuilder}
     */
    withResponseType(responseType: XMLHttpRequestResponseType): AjaxRequestBuilder;

    /**
     * @ngdoc method
     * @name AjaxRequestBuilder#withBody
     *
     * @description
     * Add request body
     *
     * @param body Request body
     * @returns {AjaxRequestBuilder}
     */
    withBody(body: any): AjaxRequestBuilder;

    /**
     * @ngdoc method
     * @name AjaxRequestBuilder#get
     *
     * @template RESPONSETYPE
     *
     * @description
     * Perform `GET` request with given params
     *
     * @param [params] Optional params
     * @returns {Observable<RESPONSETYPE>} Typed response
     */
    get<RESPONSETYPE = any>(params?: any): Observable<RESPONSETYPE>;

    /**
     * @ngdoc method
     * @name AjaxRequestBuilder#post
     *
     * @template RESPONSETYPE
     *
     * @description
     * Perform `POST` request with given params
     *
     * @param [params] Optional params
     * @returns {Observable<RESPONSETYPE>} Typed response
     */
    post<RESPONSETYPE = any>(params?: any): Observable<RESPONSETYPE>;

    /**
     * @ngdoc method
     * @name AjaxRequestBuilder#put
     *
     * @template RESPONSETYPE
     *
     * @description
     * Perform `PUT` request with given params
     *
     * @param [params] Optional params
     * @returns {Observable<RESPONSETYPE>} Typed response
     */
    put<RESPONSETYPE = any>(params?: any): Observable<RESPONSETYPE>;

    /**
     * @ngdoc method
     * @name AjaxRequestBuilder#delete
     *
     * @template RESPONSETYPE
     *
     * @description
     * Perform `DELETE` request with given params
     *
     * @param [params] Optional params
     * @returns {Observable<RESPONSETYPE>} Typed response
     */
    delete<RESPONSETYPE = any>(params?: any): Observable<RESPONSETYPE>;
}

/**
 * @ngdoc type
 * @name ObservableAjaxConfiguration
 * @module portaro.data-services
 *
 * @description
 * Advanced {@link service:observableAjaxService} configuration.
 */
export interface ObservableAjaxConfiguration {

    /**
     * @ngdoc property
     * @name ObservableAjaxConfiguration#returnRawAjaxResponse
     * @type {boolean=}
     *
     * @description
     * If this is true, service returns {@link https://rxjs-dev.firebaseapp.com/api/ajax/AjaxResponse Original `AjaxResponse`}, otherwise returns `Observable<RESPONSETYPE>`.
     *
     * In deep - it adds `map((ajaxResponse) => ajaxResponse.response)` to the pipe.
     */
    returnRawAjaxResponse?: boolean;
}

/**
 * @ngdoc type
 * @name AjaxRequestMethod
 * @module portaro.data-services
 * @type {string}
 *
 * @description
 * Possible AJAX HTTP methods: `'get'` | `'post'` | `'put'` | `'delete'`
 */
export type AjaxRequestMethod = 'get' | 'post' | 'put' | 'delete';

class AjaxRequestBuilderImpl implements AjaxRequestBuilder {
    private headers: AnyObject = {
        'Content-Type': 'application/json; charset=UTF-8'
    };
    private responseType: XMLHttpRequestResponseType = 'json';
    private body: any;
    private method: AjaxRequestMethod;
    private params: any;

    constructor(private url: string, private defaultConfiguration: ObservableAjaxConfiguration, private $httpParamSerializer: IHttpParamSerializer) {
    }

    public getHeaders(): AnyObject {
        return this.headers;
    }

    public getResponseType(): XMLHttpRequestResponseType {
        return this.responseType;
    }

    public getBody(): any {
        return this.body;
    }

    public getMethod(): AjaxRequestMethod {
        return this.method;
    }

    public getParams(): any {
        return this.params;
    }

    public getUrl(): string {
        return this.url;
    }

    public withConfiguration(configuration: ObservableAjaxConfiguration): AjaxRequestBuilder {
        this.defaultConfiguration = Object.assign(this.defaultConfiguration, configuration);

        return this;
    }

    public withHeaders(headers: AnyObject): AjaxRequestBuilder {
        this.headers = headers;
        return this;
    }

    public withResponseType(responseType: XMLHttpRequestResponseType): AjaxRequestBuilder {
        this.responseType = responseType;
        return this;
    }

    public withBody(body: any): AjaxRequestBuilder {
        this.body = body;
        return this;
    }

    public get<RESPONSETYPE = any>(params?: any): Observable<RESPONSETYPE> {
        this.method = 'get';
        this.params = params;

        return this.makeRequest();
    }

    public post<RESPONSETYPE = any>(params?: any): Observable<RESPONSETYPE> {
        this.method = 'post';
        this.params = params;

        return this.makeRequest();
    }

    public put<RESPONSETYPE = any>(params?: any): Observable<RESPONSETYPE> {
        this.method = 'put';
        this.params = params;

        return this.makeRequest();
    }

    public delete<RESPONSETYPE = any>(params?: any): Observable<RESPONSETYPE> {
        this.method = 'delete';
        this.params = params;

        return this.makeRequest();
    }

    private makeRequest(): Observable<any> {
        const url = this.params ? `${this.url}?${this.$httpParamSerializer(this.params)}` : this.url;

        const rawAjaxResponse$ = ajax({
            method: this.method,
            url,
            body: this.body,
            headers: this.headers,
            responseType: this.responseType
        });

        if (this.defaultConfiguration.returnRawAjaxResponse) {
            return rawAjaxResponse$;
        } else {
            return rawAjaxResponse$.pipe(
                map((ajaxResponse) => ajaxResponse.response)
            );
        }
    }
}
